import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { InvoiceFragment } from '../../../generated/graphql';

interface InvoiceRow {
  rowNumber: number;
  text: string;
  unit: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  price: number;
}

interface InvoiceRowEditorProps {
  invoice: InvoiceFragment;
  onAddRow: (row: Omit<InvoiceRow, 'price'>) => void;
  onUpdateRow: (rowNumber: number, row: Omit<InvoiceRow, 'price'>) => void;
  onRemoveRow: (rowNumber: number) => void;
  onMoveRowUp: (rowNumber: number) => void;
  onMoveRowDown: (rowNumber: number) => void;
  disabled?: boolean;
}

interface RowFormData {
  text: string;
  unit: string;
  quantity: string;
  unitPrice: string;
  discount: string;
}

const initialFormData: RowFormData = {
  text: '',
  unit: 'ks',
  quantity: '1',
  unitPrice: '0',
  discount: '0',
};

export function InvoiceRowEditor({
  invoice,
  onAddRow,
  onUpdateRow,
  onRemoveRow,
  onMoveRowUp,
  onMoveRowDown,
  disabled = false,
}: InvoiceRowEditorProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [formData, setFormData] = useState<RowFormData>(initialFormData);

  const rows = invoice.rows || [];

  const handleOpenDialog = (row?: InvoiceRow) => {
    if (row) {
      setEditingRow(row.rowNumber);
      setFormData({
        text: row.text,
        unit: row.unit,
        quantity: String(row.quantity),
        unitPrice: String(row.unitPrice),
        discount: String(row.discount),
      });
    } else {
      setEditingRow(null);
      // Set rowNumber to next available number
      const maxRowNumber = rows.length > 0 ? Math.max(...rows.map((r) => r.rowNumber)) : 0;
      setFormData({ ...initialFormData });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRow(null);
    setFormData(initialFormData);
  };

  const handleSave = () => {
    const rowData = {
      rowNumber: editingRow ?? (rows.length > 0 ? Math.max(...rows.map((r) => r.rowNumber)) + 1 : 1),
      text: formData.text,
      unit: formData.unit,
      quantity: parseFloat(formData.quantity) || 0,
      unitPrice: parseFloat(formData.unitPrice) || 0,
      discount: parseFloat(formData.discount) || 0,
    };

    if (editingRow !== null) {
      onUpdateRow(editingRow, rowData);
    } else {
      onAddRow(rowData);
    }
    handleCloseDialog();
  };

  const handleChange = (field: keyof RowFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const calculatePrice = (quantity: number, unitPrice: number, discount: number): number => {
    const price = quantity * unitPrice * (1 - discount / 100);
    return Math.round(price * 100) / 100;
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Riadky faktúry</Typography>
        {!disabled && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
            Pridať riadok
          </Button>
        )}
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Žiadne riadky
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row, index) => (
              <React.Fragment key={row.rowNumber}>
                {/* First row: Row number and text */}
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'top', width: '60px' }}>
                    {row.rowNumber}.
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'medium' }}>{row.text}</TableCell>
                </TableRow>
                {/* Second row: All remaining fields */}
                <TableRow>
                  <TableCell />
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        MJ: <strong>{row.unit}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Množstvo: <strong>{row.quantity}</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Jedn. cena: <strong>{row.unitPrice.toFixed(4)} €</strong>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Zľava: <strong>{row.discount}%</strong>
                      </Typography>
                      <Typography variant="body2" color="primary.main">
                        Cena: <strong>{row.price.toFixed(2)} €</strong>
                      </Typography>
                      {!disabled && (
                        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => handleOpenDialog(row)} title="Upraviť">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => onRemoveRow(row.rowNumber)} title="Zmazať" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onMoveRowUp(row.rowNumber)}
                            disabled={index === 0}
                            title="Posunúť hore"
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => onMoveRowDown(row.rowNumber)}
                            disabled={index === rows.length - 1}
                            title="Posunúť dole"
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {rows.length > 0 && (
              <TableRow>
                <TableCell />
                <TableCell>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="h6">
                      Celkom: <strong>{invoice.totalAmount.toFixed(2)} €</strong>
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRow !== null ? 'Upraviť riadok' : 'Nový riadok'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Popis"
              value={formData.text}
              onChange={handleChange('text')}
              fullWidth
              required
              multiline
              rows={2}
            />
            <TextField label="Merná jednotka" value={formData.unit} onChange={handleChange('unit')} fullWidth required />
            <TextField
              label="Množstvo"
              type="number"
              value={formData.quantity}
              onChange={handleChange('quantity')}
              fullWidth
              required
              inputProps={{ step: '0.01' }}
            />
            <TextField
              label="Jednotková cena (€)"
              type="number"
              value={formData.unitPrice}
              onChange={handleChange('unitPrice')}
              fullWidth
              required
              inputProps={{ step: '0.0001' }}
            />
            <TextField
              label="Zľava (%)"
              type="number"
              value={formData.discount}
              onChange={handleChange('discount')}
              fullWidth
              inputProps={{ step: '0.01', min: '0', max: '100' }}
            />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Vypočítaná cena:{' '}
                <strong>
                  {calculatePrice(
                    parseFloat(formData.quantity) || 0,
                    parseFloat(formData.unitPrice) || 0,
                    parseFloat(formData.discount) || 0,
                  ).toFixed(2)}{' '}
                  €
                </strong>
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Zrušiť</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.text.trim()}>
            {editingRow !== null ? 'Uložiť' : 'Pridať'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
